import React, { useState } from 'react';
import '../styles/EnrollmentStep1.css';

export interface EnrollmentStep1Props {
  companyId: string;
  siteId: number;
  packageId: number;
  apiUrl?: string;
  onSuccess: (enrollmentKey: string, personId?: string, accountId?: string, email?: string) => void;
  onCancel?: () => void;
  recaptchaSiteKey?: string;
  /** JWT of the already-authenticated caller (e.g. a logged-in PWA member).
   * When present, sent as a Bearer token so the backend can resolve the
   * existing PersonID instead of matching by email text alone. Omit for the
   * anonymous guest-enrollment flow (FitTrackOnline). */
  authToken?: string;
  prefill?: {
    firstName?: string;
    lastName?: string;
    email?: string;
    cellPhoneNumber?: string;
  };
}

interface SignupFormData {
  firstName: string;
  lastName: string;
  email: string;
  cellPhoneNumber: string;
  hasPermitted: boolean;
  promoCode: string;
}

const EnrollmentStep1: React.FC<EnrollmentStep1Props> = ({
  companyId,
  siteId,
  packageId,
  apiUrl = '',
  onSuccess,
  onCancel,
  recaptchaSiteKey,
  authToken,
  prefill
}) => {
  const [formData, setFormData] = useState<SignupFormData>({
    firstName: prefill?.firstName ?? '',
    lastName: prefill?.lastName ?? '',
    email: prefill?.email ?? '',
    cellPhoneNumber: prefill?.cellPhoneNumber ?? '',
    hasPermitted: false,
    promoCode: ''
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.firstName.trim()) {
      newErrors.firstName = 'First name is required';
    }

    if (!formData.lastName.trim()) {
      newErrors.lastName = 'Last name is required';
    }

    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Invalid email address';
    }

    if (!formData.cellPhoneNumber.trim()) {
      newErrors.cellPhoneNumber = 'Phone number is required';
    }

    if (!formData.hasPermitted) {
      newErrors.hasPermitted = 'You must agree to the terms and conditions';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const formatPhoneNumber = (value: string): string => {
    const cleaned = value.replace(/\D/g, '');
    const match = cleaned.match(/^(\d{0,3})(\d{0,3})(\d{0,4})$/);
    if (match) {
      let formatted = '';
      if (match[1]) formatted = `(${match[1]}`;
      if (match[2]) formatted += `)${match[2]}`;
      if (match[3]) formatted += `-${match[3]}`;
      return formatted;
    }
    return value;
  };

  const handleChange = (field: keyof SignupFormData, value: any) => {
    if (field === 'cellPhoneNumber') {
      value = formatPhoneNumber(value);
    }

    setFormData(prev => ({ ...prev, [field]: value }));

    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const getRecaptchaToken = async (): Promise<string> => {
    // If reCAPTCHA is not configured, return empty string
    if (!recaptchaSiteKey || !window.grecaptcha) {
      return '';
    }

    try {
      const token = await window.grecaptcha.execute(recaptchaSiteKey, { action: 'signup' });
      return token;
    } catch (error) {
      console.error('reCAPTCHA error:', error);
      return '';
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrorMessage('');

    if (!validateForm()) {
      return;
    }

    try {
      setIsSubmitting(true);

      // Get reCAPTCHA token
      const recaptchaToken = await getRecaptchaToken();

      const url = apiUrl ? `${apiUrl}/api/packageenrollment/signup` : '/api/packageenrollment/signup';
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Tenant-ID': companyId,
          ...(authToken ? { Authorization: `Bearer ${authToken}` } : {})
        },
        body: JSON.stringify({
          siteId,
          packageId,
          ...formData,
          recaptchaToken,
          minorHasValue: false,
          firstNameMinor: null,
          lastNameMinor: null
        })
      });

      const result = await response.json();
      console.error('Signup response:', response.status, result);

      if (response.ok && result.success) {
        // Pass back the email actually submitted (the user may have edited a
        // prefilled value) so callers don't fall back to a stale URL copy
        // for anything keyed on this person's email later (e.g. auto-booking
        // the class they originally tried to reserve before enrolling).
        onSuccess(result.enrollmentKey, result.personId, result.accountId, formData.email);
      } else {
        setErrorMessage(result.message || result.title || 'Failed to process signup');
      }
    } catch (error: any) {
      console.error('Signup fetch error:', error);
      setErrorMessage(error.message || 'An error occurred during signup');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="enrollment-step1-container">
      <div className="enrollment-step1-header">
        <h1>Sign Up</h1>
      </div>

      <form onSubmit={handleSubmit} className="enrollment-step1-form">
        <div className="form-group">
          <input
            type="text"
            className={`form-control ${errors.firstName ? 'error' : ''}`}
            placeholder="First Name"
            value={formData.firstName}
            onChange={(e) => handleChange('firstName', e.target.value)}
            disabled={isSubmitting}
            autoCapitalize="words"
            autoComplete="given-name"
            enterKeyHint="next"
          />
          {errors.firstName && <span className="error-text">{errors.firstName}</span>}
        </div>

        <div className="form-group">
          <input
            type="text"
            className={`form-control ${errors.lastName ? 'error' : ''}`}
            placeholder="Last Name"
            value={formData.lastName}
            onChange={(e) => handleChange('lastName', e.target.value)}
            disabled={isSubmitting}
            autoCapitalize="words"
            autoComplete="family-name"
            enterKeyHint="next"
          />
          {errors.lastName && <span className="error-text">{errors.lastName}</span>}
        </div>

        <div className="form-group">
          <input
            type="tel"
            inputMode="tel"
            className={`form-control ${errors.cellPhoneNumber ? 'error' : ''}`}
            placeholder="Phone"
            value={formData.cellPhoneNumber}
            onChange={(e) => handleChange('cellPhoneNumber', e.target.value)}
            disabled={isSubmitting}
            readOnly={!!prefill?.cellPhoneNumber}
            maxLength={13}
            autoComplete="tel"
            enterKeyHint="next"
          />
          {errors.cellPhoneNumber && <span className="error-text">{errors.cellPhoneNumber}</span>}
        </div>

        <div className="form-group">
          <input
            type="email"
            inputMode="email"
            className={`form-control ${errors.email ? 'error' : ''}`}
            placeholder="Email"
            value={formData.email}
            onChange={(e) => handleChange('email', e.target.value)}
            disabled={isSubmitting}
            autoCapitalize="none"
            autoComplete="email"
            enterKeyHint="done"
          />
          {errors.email && <span className="error-text">{errors.email}</span>}
        </div>

        <div className="form-group">
          <input
            type="text"
            className="form-control"
            placeholder="Promo Code (optional)"
            value={formData.promoCode}
            onChange={(e) => handleChange('promoCode', e.target.value)}
            disabled={isSubmitting}
            autoCapitalize="characters"
            enterKeyHint="next"
          />
        </div>

        <div className="form-group checkbox-group">
          <label>
            <input
              type="checkbox"
              checked={formData.hasPermitted}
              onChange={(e) => handleChange('hasPermitted', e.target.checked)}
              disabled={isSubmitting}
            />
            I understand and agree to the terms and conditions
          </label>
          {errors.hasPermitted && <span className="error-text">{errors.hasPermitted}</span>}
        </div>

        {errorMessage && <div className="error-message">{errorMessage}</div>}

        <div className="form-actions">
          {onCancel && (
            <button type="button" onClick={onCancel} className="btn-secondary" disabled={isSubmitting}>
              Cancel
            </button>
          )}
          <button type="submit" className="btn-primary" disabled={isSubmitting}>
            {isSubmitting ? 'Processing...' : 'Next'}
          </button>
        </div>

      </form>
    </div>
  );
};

export default EnrollmentStep1;

// Extend Window interface for reCAPTCHA
declare global {
  interface Window {
    grecaptcha: any;
  }
}
